import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Notifications as NotificationsIcon,
  CardMembership as CardMembershipIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import TourTrigger from '../tour/TourTrigger';
import SuperAdminLayoutTour from '../tour/SuperAdminLayoutTour';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { toggleTheme, mode } = useAppTheme();
  const isDarkMode = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigationItems = [
    { 
      text: 'Super Admin Dashboard', 
      icon: <DashboardIcon />, 
      path: '/super-admin/dashboard' 
    },
    { 
      text: 'Tenant Management', 
      icon: <BusinessIcon />, 
      path: '/super-admin/tenants' 
    },
    { 
      text: 'User Management', 
      icon: <PeopleIcon />, 
      path: '/super-admin/users' 
    },
    { 
      text: 'Product Updates', 
      icon: <NotificationsIcon />, 
      path: '/super-admin/product-updates' 
    },
    { 
      text: 'Subscriptions', 
      icon: <CardMembershipIcon />, 
      path: '/super-admin/subscriptions' 
    },
    { 
      text: 'System Settings', 
      icon: <SettingsIcon />, 
      path: '/super-admin/settings' 
    }
  ];

  const handleLogout = async () => {
    await logout();
    window.location.href = '/super-admin/login';
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileSettings = () => {
    navigate('/super-admin/profile');
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleBackToMainApp = () => {
    navigate('/dashboard');
  };

  const handleDrawerCollapse = () => {
    setDrawerCollapsed(!drawerCollapsed);
  };

  const drawer = (
    <Box sx={{ position: 'relative', height: '100%' }}>
             <Box sx={{ 
         p: drawerCollapsed ? 0.5 : 2, 
         textAlign: 'center',
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         gap: drawerCollapsed ? 0.5 : 2,
         minHeight: drawerCollapsed ? 48 : 'auto'
       }}>
        {!drawerCollapsed && (
          <>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              ReportPortal
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Super Admin Portal
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToMainApp}
              sx={{ mt: 1 }}
            >
              Back to App
            </Button>
          </>
        )}
        {drawerCollapsed && (
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
            RP
          </Typography>
        )}
      </Box>
      <Divider />
      <List sx={{ padding: 0 }}>
        {navigationItems.map((item) => (
          <Tooltip 
            key={item.text}
            title={drawerCollapsed ? item.text : ''} 
            placement="right"
            disableHoverListener={!drawerCollapsed}
          >
                         <ListItem
               button
               onClick={() => handleNavigation(item.path)}
               selected={location.pathname === item.path}
               sx={{
                 '&.Mui-selected': {
                   backgroundColor: 'primary.light',
                   color: 'primary.contrastText',
                   '&:hover': {
                     backgroundColor: 'primary.main',
                   },
                 },
                 minHeight: 48,
                 px: drawerCollapsed ? 1 : 2,
                 py: 0.5,
                 justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                 '& .MuiListItemIcon-root': {
                   minWidth: drawerCollapsed ? 'auto' : 40,
                   margin: 0,
                 },
                 '& .MuiListItemText-root': {
                   margin: 0,
                   '& .MuiTypography-root': {
                     fontSize: '0.875rem',
                     fontWeight: 500,
                   },
                 },
               }}
             >
               <ListItemIcon sx={{ 
                 color: 'inherit', 
                 minWidth: drawerCollapsed ? 'auto' : 40,
                 margin: 0,
                 padding: 0,
               }}>
                 {item.icon}
               </ListItemIcon>
               {!drawerCollapsed && <ListItemText primary={item.text} />}
             </ListItem>
          </Tooltip>
        ))}
      </List>
      
      {/* Collapse/Expand Button */}
      <Box sx={{ position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
        <IconButton
          onClick={handleDrawerCollapse}
          sx={{ 
            color: 'primary.main',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
        >
          {drawerCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
             <AppBar
         position="fixed"
         sx={{
           width: { md: `calc(100% - ${drawerCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
           ml: { md: `${drawerCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
           zIndex: theme.zIndex.drawer + 1,
           transition: 'all 0.3s ease-in-out',
         }}
       >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Super Admin Portal
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Theme Toggle */}
            <Tooltip title={`Switch to ${isDarkMode ? 'Light' : 'Dark'} Mode`}>
              <IconButton
                onClick={toggleTheme}
                color="inherit"
                sx={{ mr: 1 }}
              >
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            {/* Product Tour Button */}
            <TourTrigger variant="icon" size="large" color="inherit" />
            
            <Tooltip title="Super Admin">
              <AdminPanelSettingsIcon color="inherit" data-testid="super-admin-icon" />
            </Tooltip>
            
            <IconButton
              onClick={handleMenuOpen}
              sx={{ ml: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

                    {/* Navigation Drawer */}
        <Box
          component="nav"
          sx={{ 
            width: { md: drawerCollapsed ? collapsedDrawerWidth : drawerWidth }, 
            flexShrink: { md: 0 },
            transition: 'all 0.3s ease-in-out'
          }}
        >
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s ease-in-out',
              overflow: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
        
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px', // AppBar height
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {children}
      </Box>

      {/* User menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileSettings}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          Profile Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Product Tour */}
      <SuperAdminLayoutTour />
    </Box>
  );
};

export default SuperAdminLayout; 